import { Text, UnstyledButton } from '@mantine/core';
import type { Story } from '@nelnela/contracts';
import { cultureRuns } from '../../lib/culture';

/**
 * A culture note (M6b step 8, design `3d`): a minute of English about one Georgian thing, with
 * the Georgian terms tappable.
 *
 * A separate surface from the reader, on purpose. `StoryReader` is `Transcript` with the timeline
 * removed, and everything in it — whitespace tokenization, marks from `line_item`, every token a
 * tap target — is built for Georgian. Run over English prose it would mark English words, offer
 * to gloss "feast", and cover text already in the reader's own language with tap targets. So this
 * renders plain prose and makes only the Georgian runs interactive.
 *
 * It emits **no `exposure`**, and that is the honest call rather than a shortcut. An exposure is
 * evidence that the learner met a word in context and understood enough to keep reading; an
 * English sentence containing სუფრა is not that. A tap goes through the ordinary gloss route,
 * which writes its own `lookup` — which IS evidence, of exactly the thing that happened.
 */

export interface CultureNoteProps {
  note: Pick<Story, 'sentences' | 'title'>;
  /** tap on a Georgian term → the gloss sheet */
  onTerm: (term: string, paragraphIdx: number) => void;
}

export function CultureNote({ note, onTerm }: CultureNoteProps) {
  return (
    <div
      className="nn-culture"
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: '4px 16px 24px',
        // Deliberately NOT `--nn-transcript-size` and not the serif face: this is English, and
        // the reading-surface spec's type rules are about Georgian.
        fontSize: 15,
        lineHeight: 1.65,
        color: 'var(--nn-text)',
      }}
    >
      {note.sentences.map((s, i) => (
        <p key={s.idx} style={{ margin: i === 0 ? '0 0 14px' : '0 0 14px' }}>
          {cultureRuns(s.text).map((run, j) =>
            run.term ? (
              <UnstyledButton
                key={j}
                component="span"
                onClick={() => onTerm(run.text, s.idx)}
                style={{
                  // The one mark this surface has, and it is the transcript's "chunk" style: a
                  // solid underline meaning "one tap target" (reading-surface-spec §2). Reusing
                  // it means a tappable thing looks the same everywhere in the app.
                  display: 'inline',
                  fontFamily: "'Noto Sans Georgian', sans-serif",
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationThickness: '1px',
                  textUnderlineOffset: '.2em',
                  textDecorationSkipInk: 'none',
                  color: 'var(--nn-text-strong)',
                  cursor: 'pointer',
                }}
              >
                {run.text}
              </UnstyledButton>
            ) : (
              <span key={j}>{run.text}</span>
            ),
          )}
        </p>
      ))}
      <Text fz={11} c="var(--nn-faint)" mt={4}>
        Tap a Georgian word to look it up.
      </Text>
    </div>
  );
}
